import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { ConfigProvider, Layout, theme } from 'antd';
import { App as AntApp } from 'antd';
import en from 'antd/locale/en_GB';
import zh from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/zh-cn';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { NuqsAdapter } from 'nuqs/adapters/react';

import { Noti } from '@/antds/Noti.tsx';
import { getUserPermissions } from '@/apis/role.api.ts';
import ErrorBoundary from '@/components/ErrorBoundary';
import i18n from '@/i18n';
import type { LocaleName } from '@/models';
import { useRbacInit } from '@/rbac';
import { fetchRbac } from '@/stores/rbac.store.ts';

import queryClient from '../req/query-client.ts';
import { router } from '../router.tsx';
import { useLocaleName, useThemeName } from '../stores/setting.store.ts';

dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(duration);
dayjs.extend(timezone);

const ANTD_LOCALE_MAP: Record<LocaleName, typeof en> = {
  'en-GB': en,
  'zh-CN': zh,
  // Ant Design has no built-in cy-GB locale package. Use en-GB as fallback.
  'cy-GB': en,
};

const DAYJS_LOCALE_MAP: Record<LocaleName, string> = {
  'en-GB': 'en-gb',
  'zh-CN': 'zh-cn',
  // Day.js has no built-in cy-GB locale package. Use en-gb for consistency.
  'cy-GB': 'en-gb',
};

export default function App() {
  const themeName = useThemeName();
  const localeName = useLocaleName();
  const user = useUser();
  useRbacInit({
    user: user as any,
    getUserUisApi: getUserPermissions,
  });

  useEffect(() => {
    if (user?.accessToken) {
      fetchRbac();
    }
  }, [user?.accessToken]);

  useEffect(() => {
    dayjs.locale(DAYJS_LOCALE_MAP[localeName] ?? 'en-gb');
    if (i18n.language !== localeName) {
      void i18n.changeLanguage(localeName);
    }
  }, [localeName]);

  useEffect(() => {
    if (themeName === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [themeName]);

  return (
    <div className={themeName} id={'app'}>
      <>
        <NuqsAdapter>
          <QueryClientProvider client={queryClient}>
            <ConfigProvider
              tooltip={{
                unique: false,
              }}
              locale={ANTD_LOCALE_MAP[localeName] ?? en}
              theme={{
                hashed: false,
                zeroRuntime: true,
                token: { motion: false },
                algorithm:
                  themeName === 'dark'
                    ? theme.darkAlgorithm
                    : theme.defaultAlgorithm,
                components: {
                  Card: {
                    bodyPadding: 16,
                  },
                },
              }}
            >
              <AntApp>
                <Layout>
                  <ErrorBoundary>
                    <RouterProvider router={router} />
                  </ErrorBoundary>
                  <Noti />
                </Layout>
              </AntApp>
            </ConfigProvider>
          </QueryClientProvider>
        </NuqsAdapter>
      </>
    </div>
  );
}
